import * as React from 'react';
import { connect } from 'react-redux';
import { HashRouter, Route, RouteComponentProps } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { DataRoleSpaceRouter } from '../../../dataRole/components/DataRoleSpaceRouter';
import { RedirectToFavoritedDataRoleProvider } from '../../../dataRole/components/RedirectToFavoritedDataRoleProvider';
import { FlowSpaceRouter } from '../../../flow/components/FlowSpaceRouter';
import { HomeSpaceProvider } from '../../../home/components/HomeSpaceProvider';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { AuthLevel, ServerService } from '../../../ts/services/ServerService';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { SiteSpaceContentType } from '../../state/SiteSpaceContentTypeState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { BackgroundJobsSpaceProvider } from '../BackgroundJobsSpace/BackgroundJobsSpaceProvider';
import { AddUserToDataAlertProvider, RemoveUserFromDataAlertProvider } from '../DataAlerts/AddRemoveDataAlertProvider';
import { DatasourceSpaceRouter } from '../DatasourceSpace/DatasourceSpaceRouter';
import { ExploreDatasourceProvider } from '../DatasourceSpace/ExploreDatasourceProvider';
import { RedirectToFavoritedDatasourceProvider } from '../DatasourceSpace/RedirectToFavoritedDatasourceProvider';
import { ConnectedFavoritesSpace } from '../FavoritesSpace/ConnectedFavoritesSpace';
import { GroupSpaceRouter } from '../groups/GroupSpaceRouter';
import { MetricsSpaceRouter } from '../MetricsSpace/MetricsSpaceRouter';
import { ProjectSpaceRouter } from '../ProjectSpace/ProjectSpaceRouter';
import { RedirectToFavoritedProjectProvider } from '../ProjectSpace/RedirectToFavoritedProjectProvider';
import { RecentsSpaceProvider } from '../RecentsSpace/RecentsSpaceProvider';
import { RecommendationsSpaceProvider } from '../RecommendationSpace/RecommendationsSpaceProvider';
import { ScheduleSpaceRouter } from '../Schedules/ScheduleRouter';
import { AllSchedulesSpaceProvider } from '../Schedules/SchedulesSpaceProvider';
import { ConnectedSearchSpaceRouter } from '../Search/SearchSpaceRouter';
import { SiteSpaceProvider, SiteSpaceProviderProps } from '../SiteSpace/SiteSpaceProvider';
import { SiteStatusSpaceProvider } from '../SiteStatusSpace/SiteStatusSpaceProvider';
import { SiteTasksSpaceRouter } from '../SiteTasksSpace/SiteTasksSpaceRouter';
import { SiteUserSpaceRouter } from '../SiteUserSpace/SiteUserSpaceRouter';
import { AllSiteUsersSpaceProvider } from '../SiteUsersSpace/AllSiteUsersSpaceProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { RedirectToViewProvider, RedirectViewType } from '../VizSpace/RedirectToViewProvider';
import { RedirectToFavoritedWorkbookProvider } from '../WorkbookSpace/RedirectToFavoritedWorkbookProvider';
import { WorkbookSpaceRouter } from '../WorkbookSpace/WorkbookSpaceRouter';
import { RouterError } from './RouterError';
import { TableauReactRouterSwitch } from './TableauReactRouterSwitch';

export interface SiteSpaceRouterOwnProps extends ReactDirectiveProps {}

export interface SiteSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
}

export type SiteSpaceRouterProps = SiteSpaceRouterOwnProps & SiteSpaceRouterStateProps;

export class SiteSpaceRouterComponent extends React.PureComponent<SiteSpaceRouterProps> {
  private server: ServerService;

  public constructor(props: SiteSpaceRouterProps) {
    super(props);
    this.server = ServiceLocator.get<ServerService>('ServerService');
  }

  private memoizedGetWatchedBasePaths = defaultMemoize(SiteSpaceRouterComponent.getWatchedBasePaths);
  public render() {
    const basePaths = this.memoizedGetWatchedBasePaths(this.props.urls);
    return (
      <HashRouter>
        <TableauReactRouterSwitch basePaths={basePaths} urls={this.props.urls}>
          { FeatureFlags.searchSpace &&
            <Route path={this.props.urls.searchPath()} render={() => <ConnectedSearchSpaceRouter {...this.props}/>} />
          }
          { FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled &&
            <Route exact path={this.props.urls.dataRolesPath()} render={this.siteSpaceprovider('dataRole')} />
          }
          <Route exact path={this.props.urls.projectsPath()} render={this.siteSpaceprovider('project')} />
          <Route exact path={this.props.urls.explorePath()} render={this.siteSpaceprovider('explore')} />
          { this.server.flowsOnSiteEnabled() &&
            <Route exact path={this.props.urls.flowsPath()} render={this.siteSpaceprovider('flow')} />
          }
          <Route exact path={this.props.urls.viewsPath()} render={this.siteSpaceprovider('view')} />
          <Route path={this.props.urls.redirectToFavoriteViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'favorite')} />
          <Route path={this.props.urls.redirectToViewPath(':id')} render={({ match }) => this.redirectToViewProvider(match.params.id, 'regular')} />

          <Route exact path={this.props.urls.workbooksPath()} render={this.siteSpaceprovider('workbook')} />
          <Route path={this.props.urls.workbookPath(':id')} render={() => <WorkbookSpaceRouter {...this.props} />} />
          <Route exact path={this.props.urls.redirectToWorkbookPath(':id')} render={({ match }) => this.redirectToFavoritedWorkbookProvider(match.params.id)} />

          { this.getGroupsRoute(this.server) }

          <Route exact path={this.props.urls.datasourcesPath()} render={this.siteSpaceprovider('datasource')} />
          <Route path={this.props.urls.datasourcePath(':id')} render={() => <DatasourceSpaceRouter {...this.props} />} />
            {/* // explore/datasource navigates to the data source by its Published Datasource ID, rather than the Unified Datasource (connection) ID */}
          <Route exact path={this.props.urls.exploreDatasourcePath(':id')} render={({ match }) => this.exploreDatasourceProvider(match.params.id)} />
          <Route exact path={this.props.urls.favoriteDatasourcePath(':id', ':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting, match.params.id)} />          }
          <Route exact path={this.props.urls.favoriteDatasourceDoesntExistPath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDatasourceProvider(match.params.idForFavoriting)} />

          <Route path={this.props.urls.flowPath(':id')} render={routeProps => <FlowSpaceRouter {...this.props} routeLocationSearch={routeProps.location.search} routeLocationPath={routeProps.location.pathname}/>}/>

          { this.getUsersRoute() }
          <Route path={this.props.urls.baseUserPath()} render={() => <SiteUserSpaceRouter {...this.props} />} />

          <Route path={this.props.urls.projectPath(':id')} render={() => <ProjectSpaceRouter {...this.props} />} />
          <Route exact path={this.props.urls.redirectToProjectPath(':id')} render={({ match }) => this.redirectToFavoritedProjectProvider(match.params.id)} />

          <Route path={this.props.urls.favoritesPath()} render={this.favoritesSpace} />
          <Route path={this.props.urls.recentsPath()} render={this.recentsSpace} />
          { FeatureFlags.homePage &&
            <Route path={this.props.urls.homePath()} render={() => <HomeSpaceProvider {...this.props} />} />
          }
          <Route path={this.props.urls.recommendationListPath()} render={() => <RecommendationsSpaceProvider {...this.props}/>}/>
          { FeatureFlags.metricsSpaceEnabled &&
            <Route path={this.props.urls.metricsPath()} render={() => <MetricsSpaceRouter {...this.props} />} />
          }
          {
            FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled &&
            <Route path={this.props.urls.dataRolePath(':id')} render={() => <DataRoleSpaceRouter {...this.props} />} />
          }
          {
            FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled &&
            <Route path={this.props.urls.redirectToDataRolePath(':idForFavoriting')} render={({ match }) => this.redirectToFavoritedDataRoleProvider(match.params.idForFavoriting)} />
          }
          { this.getSiteStatusRoute(this.server) }
          { FeatureFlags.stylishTasks && this.getSiteTasksRoute(this.server) }
          {
            FeatureFlags.jobManagementEnabled &&
            this.getSiteBackgroundJobsRoute(this.server)
          }
          {
            this.getScheduleRoute(this.server)
          }
          {
            this.getAllSchedulesRoute(this.server)
          }
          <Route path={this.props.urls.addUserToDataAlertPath(':dataAlertId')} render={({ match }) => <AddUserToDataAlertProvider dataAlertId={match.params.dataAlertId} angularContext={this.props.angularContext}/>} />
          <Route path={this.props.urls.removeUserFromDataAlertPath(':dataAlertId')} render={({ match }) => <RemoveUserFromDataAlertProvider dataAlertId={match.params.dataAlertId} angularContext={this.props.angularContext}/>} />
          <Route render={this.error}/>
        </TableauReactRouterSwitch>
      </HashRouter>
    );
  }

  private getSiteBackgroundJobsRoute(server: ServerService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.jobsPath()}
             render={(routeProps) => <BackgroundJobsSpaceProvider
               angularContext={this.props.angularContext}
               routeLocationSearch={routeProps.location.search}
               routeLocationPath={routeProps.location.pathname}
             />}
      />;
  }

  private getAllSchedulesRoute(server: ServerService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route exact path={this.props.urls.schedulesPath()}
             render={(routeProps) => <AllSchedulesSpaceProvider
               angularContext={this.props.angularContext}
               routeLocationSearch={routeProps.location.search}
               routeLocationPath={routeProps.location.pathname}
               urls={this.props.urls}
             />}
      />;
  }

  private getScheduleRoute(server: ServerService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.schedulePath(':id')} render={() => <ScheduleSpaceRouter {...this.props} />} />;
  }

  private siteSpaceprovider = (siteSpaceContentType: SiteSpaceContentType): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const siteSpaceProviderProps: SiteSpaceProviderProps = {
        siteSpaceContentType,
        routeLocationSearch: props.location.search,
        routeLocationPath: props.location.pathname,
        urls: this.props.urls,
        angularContext: this.props.angularContext,
      };

      return <SiteSpaceProvider {...siteSpaceProviderProps} key={'1'}/>;
    };
  }

  private favoritesSpace = (props: RouteComponentProps): JSX.Element => (
    <ConnectedFavoritesSpace
      angularContext={this.props.angularContext}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )

  private recentsSpace = (props: RouteComponentProps): JSX.Element => (
    <RecentsSpaceProvider
      angularContext={this.props.angularContext}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )

  private exploreDatasourceProvider = (publishedDatasourceId: string): JSX.Element => (
    <ExploreDatasourceProvider publishedDatasourceId={publishedDatasourceId} urls={this.props.urls} />
  )

  private redirectToFavoritedDataRoleProvider = (roleId?: string): JSX.Element => (
    <RedirectToFavoritedDataRoleProvider roleId={roleId} urls={this.props.urls}/>
  )

  private redirectToFavoritedDatasourceProvider = (idForFavoriting: string, datasourceId?: string): JSX.Element => (
    <RedirectToFavoritedDatasourceProvider idForFavoriting={idForFavoriting} datasourceId={datasourceId} urls={this.props.urls} />
  )

  private redirectToFavoritedWorkbookProvider = (workbookId?: string): JSX.Element => (
    <RedirectToFavoritedWorkbookProvider workbookId={workbookId} urls={this.props.urls} />
  )

  private redirectToFavoritedProjectProvider = (projectId?: string): JSX.Element => (
    <RedirectToFavoritedProjectProvider projectId={projectId} urls={this.props.urls} />
  )

  private redirectToViewProvider = (viewId: string, redirectType: RedirectViewType): JSX.Element => (
    <RedirectToViewProvider viewId={viewId} urls={this.props.urls} redirectType={redirectType} />
  )

  private error = (): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls}/>;
  }

  private getGroupsRoute(server: ServerService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.groupsPath()} render={() => <GroupSpaceRouter {...this.props} />} />;
  }

  private getSiteStatusRoute(server: ServerService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route
        path={this.props.urls.statusPath()}
        render = {() =>
          <SiteStatusSpaceProvider
            angularContext={this.props.angularContext}
            urls={this.props.urls}
          />
        }
      />;
  }

  private getUsersRoute(): React.ReactNode {
    return this.server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.usersPath()} render={(routeProps) =>
        <AllSiteUsersSpaceProvider
          angularContext={this.props.angularContext}
          routeLocationPath={routeProps.location.pathname}
          routeLocationSearch={routeProps.location.search}
          urls={this.props.urls}
        />}
      />;
  }

  private getSiteTasksRoute(server: ServerService): React.ReactNode {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={this.props.urls.tasksPath()} render={() => <SiteTasksSpaceRouter {...this.props} />} />;
  }

  public static getWatchedBasePaths(urls: SpacesSiteUrls) {
    return _.compact([
      urls.baseUserPath(),
      urls.usersPath(),
      urls.favoritesPath(),
      urls.recentsPath(),
      urls.projectPath(''),
      urls.datasourcePath(''),
      FeatureFlags.flowSpaceReactRouter && urls.flowPath(''),
      urls.workbookPath(''),
      urls.workbooksPath(),
      urls.dataRolesPath(),
      urls.searchPath(),
      urls.groupsPath(),
      urls.homePath(),
      urls.recommendationListPath(),
      FeatureFlags.metricsSpaceEnabled && urls.metricsPath(),
      FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled && urls.dataRolePath(''),
      urls.statusPath(),
      urls.explorePath(),
      urls.projectsPath(),
      urls.flowsPath(),
      urls.viewsPath(),
      urls.datasourcesPath(),
      FeatureFlags.stylishTasks && urls.tasksPath(),
      FeatureFlags.jobManagementEnabled && urls.jobsPath(),
      urls.schedulesPath(),
      urls.redirectToViewPath(''),
      urls.redirectToFavoriteViewPath(''),
      urls.redirectToDataRolePath(''),
      urls.redirectToWorkbookPath(''),
      urls.exploreDatasourcePath(''),
      urls.favoriteDatasourcePath('', ''),
      urls.metricPath(''),
      urls.dataAlertPath(), // for remove and add data alerts
    ]);
  }
}



const mapStateToProps: MapStateToProps<SiteSpaceRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state)
});

export const SiteSpaceRouter: React.ComponentClass<SiteSpaceRouterOwnProps>
  = connect(mapStateToProps)(SiteSpaceRouterComponent);
